import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Toolbar,
  useColorScheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import darkLogo from '../../assets/SEICLogoDark.svg';
import lightLogo from '../../assets/SEICLogoLight.svg';
import { useUser } from '../../hooks/useUser';
import AppNav from './AppNav';

import { Menu, MenuOpen } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from '../ErrorBoundary';
import ServiceWorkerPrompt from '../ServiceWorkerPrompt';
import UserLicenseDialog from '../UserLicense/UserLicenseDialog';
import AuthWrapper from './AuthWrapper';
import FeedbackPopup from '../FeedbackPopup/FeedbackPopup';

export type AppLayoutProps = {
  children: React.ReactNode;
  onThemeChange?: (theme: string) => void;
  onNavigate?: (path: string) => void;
  onLogout?: () => void;
};

const AppLayout = ({ children, ...props }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { isAuthenticated, isLoading, details, nav } = useUser();
  const { breakpoints, palette } = useTheme();
  const { mode, setMode } = useColorScheme();
  const isMobile = useMediaQuery(breakpoints.only('xs'));
  const isPalm = useMediaQuery(breakpoints.only('sm'));
  const isTablet = useMediaQuery(breakpoints.only('md'));
  const location = useLocation();
  const navigate = useNavigate();

  const userSelectedDrawerOpen = details?.drawerOpen ?? true;
  const isOpen = userSelectedDrawerOpen && !isMobile; //&& !isPalm && !isTablet;

  const theme = details?.theme ?? 'dark';

  useEffect(() => {
    if (isAuthenticated && theme) {
      setMode(theme);
    } else if (!isAuthenticated) {
      setMode('dark');
    }
  }, [isAuthenticated, theme, setMode]);

  useEffect(() => {
    if (!isMobile || !isAuthenticated) {
      setMobileNavOpen(false);
    }
  }, [isMobile, isAuthenticated]);

  return (
    <ErrorBoundary type="app">
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          width: '100vw',
          flexDirection: isMobile ? 'column' : 'row',
          color: palette.text.primary,
          // Updated scrollbar styles for dark theme
          '& ::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '& ::-webkit-scrollbar-track': {
            background: palette.opac1, // was #2d405e
            borderRadius: '10px',
          },
          '& ::-webkit-scrollbar-thumb': {
            background: palette.opac2, // was #121c29
            borderRadius: '10px',
          },
          '& ::-webkit-scrollbar-thumb:hover': {
            background: palette.background.paperElevation2, // was #0f1926
          },
          '& .fade-in-page': {
            animation: 'fadeInPage 0.8s',
          },
          '@keyframes fadeInPage': {
            from: { opacity: 0, transform: 'translateY(8px)' },
            to: { opacity: 1, transform: 'none' },
          },
          '& .MuiDataGrid-root': {
            backgroundColor: palette.surface1,
            border: 'none',
            fontWeight: 400,
            fontSize: 14,
            '--DataGrid-t-color-border-base': palette.divider,
            '& .MuiDataGrid-columnHeader--filledGroup': {
              textAlign: 'center',
              textTransform: 'capitalize',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid',
              borderColor: palette.background.paperElevation1,
              color: palette.text.secondary,
            },
            '& .MuiDataGrid-columnHeaders': { background: palette.surface2 },
            '& .MuiDataGrid-columnsManagement': { color: palette.text.primary },
          },
        }}
      >
        {isAuthenticated && isMobile && (
          <>
            <AppBar
              sx={{
                height: 72,
                background: palette.background.paperElevation0, // was fixed color
                boxShadow: palette.shadows[25],
              }}
              position="sticky"
              color="primary"
            >
              <Toolbar
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  gap: '20px',
                }}
              >
                <IconButton onClick={() => setMobileNavOpen(!mobileNavOpen)}>
                  {mobileNavOpen ? <MenuOpen /> : <Menu />}
                </IconButton>
                <div>
                  <img
                    src={details?.theme === 'dark' ? darkLogo : lightLogo}
                    alt="logo"
                    style={{ height: '40px', width: '100%' }}
                  />
                </div>
              </Toolbar>
            </AppBar>

            <Drawer
              open={mobileNavOpen}
              onClose={() => setMobileNavOpen(false)}
              anchor="left"
              sx={{
                zIndex: (theme) => theme.zIndex.appBar - 1,
                '& .MuiDrawer-paper': {
                  top: 72,
                  height: 'calc(100% - 72px)',
                },
              }}
            >
              <AppNav
                open
                items={nav}
                {...props}
                onNavigate={(...args) => {
                  setMobileNavOpen(false);
                  navigate(...args);
                }}
              />
            </Drawer>
          </>
        )}
        {isAuthenticated && !isMobile && <AppNav open={isOpen} items={nav} {...props} />}
        <Box
          key={location.pathname}
          component="main"
          sx={{
            background: palette.background.default, // was fixed color
            flex: 1,
            //marginTop: isMobile ? "80px" : 0,
            overflow: 'auto',
          }}
        >
          <Box key={location.pathname} className="fade-in-page">
            <AuthWrapper>
              <Outlet />
            </AuthWrapper>
          </Box>
        </Box>
      </Box>
      {isAuthenticated && import.meta.env.PROD && <ServiceWorkerPrompt />}
      {isAuthenticated && <UserLicenseDialog />}
      {isAuthenticated && <FeedbackPopup />}
    </ErrorBoundary>
  );
};
export default AppLayout;
